import React, { Component, ErrorInfo, ReactNode } from "react";
import { Anchor, Box, Button, Notification, ThemeProps } from "@deliverr/ui";
import styled from "@emotion/styled";
import { FormattedMessage } from "react-intl";
import { MailToFreight } from "../common/linkToWebsite";
import { maxWidthCenterNarrowStyle } from "../common/styleUtils";

const Container = styled(Box)<ThemeProps>(
  () => `
    ${maxWidthCenterNarrowStyle}
    padding: 24px;
  `
);

const RefreshButton = styled(Button)<ThemeProps>(
  ({ theme }) => `
    margin-top: ${theme.spacing.S4};
  `
);

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("Unhandled UI error:", error, errorInfo);
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <Container>
        <Notification
          appearance="DANGER"
          content={{
            title: (
              <FormattedMessage
                id="errorBoundary.title"
                defaultMessage="Something went wrong"
              />
            ),
            description: (
              <FormattedMessage
                id="errorBoundary.description"
                defaultMessage="If the problem continues, reach out to {email}."
                values={{
                  email: (
                    <Anchor href={MailToFreight}>freight@deliverr.com</Anchor>
                  ),
                }}
              />
            ),
          }}
        />
        <RefreshButton onClick={() => window.location.reload()}>
          <FormattedMessage
            id="errorBoundary.refresh"
            defaultMessage="Refresh the page"
          />
        </RefreshButton>
      </Container>
    );
  }
}
